import React, { createContext, ReactNode, useContext, useCallback, useEffect, useRef } from "react";
import useLocalStorageState from "use-local-storage-state";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import axios from "../lib/axiosConfig";
import fetcher from "../lib/fetcher";
import { isTokenExpired, msUntilExpiry } from "../lib/tokenUtils";
import { resetVersionState } from "../lib/versionSync";
import { APIBaseURL, LOGIN_URL } from "../utils/constant";
import { devLog } from "../utils/libs";

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Utility function to validate token
const authValidateToken = async (): Promise<boolean> => {
  try {
    const response = await axios.get("/api/v1/user");
    return response.status === 200;
  } catch (error) {
    console.error("Token validation error:", error);
    return false;
  }
};

// Helpers to read from localStorage synchronously
function getJSON<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

// Module-level deduplication: only one refresh in-flight at a time across the app
let refreshPromise: Promise<AuthTokens | null> | null = null;

// Refresh buffer: schedule refresh 5 minutes before expiry
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

/**
 * Call the guest /refresh-token endpoint using raw fetch (not axios)
 * to avoid triggering the 401 interceptor recursively.
 */
async function callRefreshEndpoint(
  refreshToken: string,
  email: string
): Promise<{ AccessToken: string; IdToken: string } | null> {
  try {
    const response = await fetch(`${APIBaseURL}/refresh-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken, email }),
    });

    if (!response.ok) return null;

    const json = await response.json();
    if (json.success && json.data) {
      return json.data as { AccessToken: string; IdToken: string };
    }
    return null;
  } catch {
    return null;
  }
}

// Implement provider
const useProvideAuth = (): AuthContextType => {
  // Initialize user and tokens state
  const [user, setUser] = useLocalStorageState<User | null>("pd-user", {
    defaultValue: getJSON<User>("pd-user"),
  });
  const [tenantUser, setTenantUser] = useLocalStorageState<TenantUser | null>(
    "pd-tenantUser",
    {
      defaultValue: null,
    }
  );
  const [tokens, setTokens] = useLocalStorageState<AuthTokens | null>(
    "pd-tokens",
    { defaultValue: getJSON<AuthTokens>("pd-tokens") }
  );
  const [roles] = useLocalStorageState<string[]>("roles", {
    defaultValue: [],
  });

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep stable refs for values used in callbacks to avoid stale closures
  const tokensRef = useRef(tokens);
  tokensRef.current = tokens;
  const userRef = useRef(user);
  userRef.current = user;

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const removeAuth = useCallback(() => {
    clearRefreshTimer();
    refreshPromise = null;
    setUser(null);
    setTokens(null);
    setTenantUser(null);
    resetVersionState();
    if (Capacitor.isNativePlatform()) {
      Preferences.remove({ key: "pd-tokens" });
      Preferences.remove({ key: "pd-user" });
      Preferences.remove({ key: "pd-tenantUser" });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearRefreshTimer]);

  /**
   * Silently refresh tokens using the guest /refresh-token endpoint.
   * Deduplicates concurrent calls via a module-level promise.
   */
  const refreshTokensSilently = useCallback((): Promise<AuthTokens | null> => {
    if (refreshPromise) return refreshPromise;

    const currentTokens = tokensRef.current;
    const currentUser = userRef.current;

    if (!currentTokens?.refresh_token || !currentUser?.email) {
      removeAuth();
      window.location.href = LOGIN_URL;
      return Promise.resolve(null);
    }

    refreshPromise = callRefreshEndpoint(currentTokens.refresh_token, currentUser.email)
      .then((data) => {
        refreshPromise = null;
        if (data) {
          const newTokens: AuthTokens = {
            access_token: data.AccessToken,
            id_token: data.IdToken,
            refresh_token: currentTokens.refresh_token, // Cognito doesn't return a new refresh token
          };
          setTokens(newTokens);
          devLog("[Auth] Tokens refreshed silently");
          return newTokens;
        }
        // Refresh failed — force re-login
        removeAuth();
        window.location.href = LOGIN_URL;
        return null;
      })
      .catch(() => {
        refreshPromise = null;
        removeAuth();
        window.location.href = LOGIN_URL;
        return null;
      });

    return refreshPromise;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeAuth]);

  /**
   * Schedule a token refresh before the access token expires.
   * If already expired, attempts an immediate refresh via the guest endpoint.
   */
  const scheduleTokenRefresh = useCallback(() => {
    clearRefreshTimer();

    const currentTokens = tokensRef.current;
    if (!currentTokens?.access_token) return;

    const ms = msUntilExpiry(currentTokens.access_token);

    if (ms <= 0) {
      // Already expired — attempt recovery via refresh token
      devLog("[Auth] Token expired, attempting immediate refresh");
      refreshTokensSilently();
      return;
    }

    if (ms <= REFRESH_BUFFER_MS) {
      // Expiring very soon — refresh now
      devLog("[Auth] Token expiring soon, refreshing now");
      refreshTokensSilently();
      return;
    }

    // Schedule refresh 5 minutes before expiry
    const delay = ms - REFRESH_BUFFER_MS;
    devLog(`[Auth] Scheduling token refresh in ${Math.round(delay / 1000)}s`);
    refreshTimerRef.current = setTimeout(() => {
      refreshTokensSilently();
    }, delay);
  }, [clearRefreshTimer, refreshTokensSilently]);

  const saveAuth = (data: AuthResponse) => {
    if (data.tokens && data.user) {
      setUser(data.user);
      setTokens(data.tokens);
      if (Capacitor.isNativePlatform()) {
        Preferences.set({ key: "pd-tokens", value: JSON.stringify(data.tokens) });
        Preferences.set({ key: "pd-user", value: JSON.stringify(data.user) });
      }
    } else {
      devLog("Invalid login data");
    }
  };

  // Append a new tenant to the current user's tenant list (used after creating an organisation)
  const addTenantToUser = (tenant: Tenant) => {
    setUser((prev) => {
      if (!prev) return prev;
      const existing = prev.tenants ?? [];
      // Avoid duplicates by ID
      if (existing.some((t) => t.id === tenant.id)) return prev;
      return {
        ...prev,
        tenants: [...existing, tenant],
      };
    });
  };

  // Update an existing tenant (e.g., rename) in the current user's tenant list
  const updateTenantInUser = (tenant: Tenant) => {
    setUser((prev) => {
      if (!prev) return prev;
      const existing = prev.tenants ?? [];
      if (!existing.some((t) => t.id === tenant.id)) return prev;
      return {
        ...prev,
        tenants: existing.map((t) => (t.id === tenant.id ? { ...t, ...tenant } : t)),
      };
    });
  };

  // Remove a tenant from the current user's tenant list (after deletion)
  const removeTenantFromUser = (tenantId: number) => {
    setUser((prev) => {
      if (!prev) return prev;
      const existing = prev.tenants ?? [];
      if (!existing.some((t) => t.id === tenantId)) return prev;
      return {
        ...prev,
        tenants: existing.filter((t) => t.id !== tenantId),
      };
    });
  };

  const validateToken = async (): Promise<boolean> => {
    if (tokens && tokens.access_token) {
      const isValid = await authValidateToken();
      if (!isValid) {
        removeAuth();
      }
      return isValid;
    }
    return false;
  };

  const switchToTenant = async (tenantId: number): Promise<TenantUser> => {
    // we need to get the TenantUser information
    return new Promise<TenantUser>(async (resolve, reject) => {
      try {
        const response = await fetcher("/api/v1/user/tenant/" + tenantId);
        const data = response.data;
        setTenantUser(data);
        resolve(data);
      } catch (error) {
        console.error("Error fetching tenant user:", error);
        reject(error);
      }
    });
  };

  /**
   * Silently re-fetch the current TenantUser from the API.
   * Called by version sync when the backend reports a user version change.
   */
  const refreshTenantUser = async (): Promise<void> => {
    const currentTenantUser = tenantUser;
    if (!currentTenantUser) return;

    // Read the current tenant ID from localStorage (set by TenantContext)
    const storedTenant = getJSON<Tenant>("PD-tenant");
    if (!storedTenant?.id) return;

    try {
      const response = await fetcher("/api/v1/user/tenant/" + storedTenant.id);
      const data = response.data;
      setTenantUser(data);
      devLog("[VersionSync] TenantUser refreshed silently");
    } catch (error) {
      console.warn("[VersionSync] Failed to refresh tenant user:", error);
    }
  };

  // Schedule token refresh on mount and whenever tokens change
  useEffect(() => {
    if (tokens?.access_token) {
      scheduleTokenRefresh();
    }
    return () => clearRefreshTimer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens?.access_token]);

  // Handle sleep/wake: re-check token expiry when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && tokensRef.current?.access_token) {
        devLog("[Auth] Tab visible — checking token expiry");
        scheduleTokenRefresh();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [scheduleTokenRefresh]);

  // Handle multi-tab: when another tab refreshes tokens, reschedule our timer
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== "pd-tokens") return;

      if (e.newValue === null || e.newValue === "null") {
        // Another tab logged out
        devLog("[Auth] Another tab logged out — clearing local auth");
        clearRefreshTimer();
        refreshPromise = null;
        setUser(null);
        setTokens(null);
        setTenantUser(null);
        resetVersionState();
        return;
      }

      // Another tab refreshed tokens — reschedule our timer
      try {
        const newTokens = JSON.parse(e.newValue) as AuthTokens;
        if (newTokens?.access_token) {
          devLog("[Auth] Tokens updated from another tab — rescheduling refresh");
          tokensRef.current = newTokens;
          scheduleTokenRefresh();
        }
      } catch {
        // ignore parse errors
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleTokenRefresh, clearRefreshTimer]);

  // Startup: if token is expired, attempt immediate refresh or clear auth
  useEffect(() => {
    if (tokens?.access_token && isTokenExpired(tokens.access_token)) {
      if (tokens.refresh_token && user?.email) {
        devLog("[Auth] Startup: token expired, attempting refresh");
        refreshTokensSilently();
      } else {
        devLog("[Auth] Startup: token expired, no refresh token — logging out");
        removeAuth();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Optional: keep minimal dev logs during development
  useEffect(() => {
    devLog("Auth.user changed:", user ? "present" : null);
  }, [user]);

  useEffect(() => {
    devLog("Auth.tokens changed:", tokens?.access_token ? "present" : null);
  }, [tokens]);

  // One-time migration from legacy storage keys
  useEffect(() => {
    try {
      if (!tokens) {
        const legacy = localStorage.getItem("tokens");
        if (legacy) {
          const parsed = JSON.parse(legacy);
          if (parsed && (parsed.access_token || parsed.id_token)) {
            devLog("Auth.migration: moving legacy 'tokens' -> 'pd-tokens'");
            setTokens(parsed);
            localStorage.removeItem("tokens");
          }
        }
      }
    } catch (e) {
      console.warn("Auth.migration error:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync tokens/user to Capacitor Preferences whenever they change (native only)
  // This backs up the localStorage state to native secure storage, recovering gracefully
  // if WKWebView purges localStorage under memory pressure.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (tokens) {
      Preferences.set({ key: "pd-tokens", value: JSON.stringify(tokens) });
    }
  }, [tokens]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (user) {
      Preferences.set({ key: "pd-user", value: JSON.stringify(user) });
    }
  }, [user]);

  // On native startup: if localStorage is empty, recover from Preferences
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const recover = async () => {
      if (!tokens) {
        const { value } = await Preferences.get({ key: "pd-tokens" });
        if (value) {
          try {
            const parsed = JSON.parse(value) as AuthTokens;
            if (parsed?.access_token) {
              devLog("[Auth] Recovered tokens from Capacitor Preferences");
              setTokens(parsed);
            }
          } catch { /* ignore */ }
        }
      }
      if (!user) {
        const { value } = await Preferences.get({ key: "pd-user" });
        if (value) {
          try {
            const parsed = JSON.parse(value) as User;
            if (parsed?.id) {
              devLog("[Auth] Recovered user from Capacitor Preferences");
              setUser(parsed);
            }
          } catch { /* ignore */ }
        }
      }
    };

    recover();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (Cleanup) storage debug/self-heal removed; rely on local state + normal flows

  return {
    user,
    tokens,
    saveAuth,
    removeAuth,
    validateToken,
    refreshTokensSilently,
    roles,
    tenantUser,
    switchToTenant,
    refreshTenantUser,
    addTenantToUser,
    updateTenantInUser,
    removeTenantFromUser,
  };
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const auth = useProvideAuth();

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
